import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
  TableSortLabel, // Re-add TableSortLabel
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useQuery, gql } from "@apollo/client";
import { midgardClients, ALERT_RULES_QUERY } from "providers/MidgardProvider";
import { convertToCSV, downloadCSV } from "./configurationPageUtils";
import { CustomJsonEditor } from "components/Nav/Drawers/RightDrawer/FlightDebugger/debuggerComponents/CustomJsonEditor";
import useWindowSize from "utils/customHooks/useWindowSize";
import { startCase } from "lodash";

/**
 * Alert Rules section for displaying alert rule templates
 */
export const AlertRulesSection: React.FC = () => {
  const { translate } = useFlightTranslation();
  const cKey = useCustomerKey();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search and sort states
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortColumn, setSortColumn] = useState<string>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  // Contact groups state
  const [contactGroups, setContactGroups] = useState<Record<string, any[]>>({});

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Window dimension
  const innerHeight = useWindowSize()?.innerHeight;

  // Define GraphQL queries
  const CONTACTS_QUERY = gql`
    query ContactsQuery {
      contacts {
        contactGroups {
          description
          id
          members {
            email
          }
          name
        }
      }
    }
  `;

  // Query to get alert rule templates
  const { data, loading: queryLoading } = useQuery(ALERT_RULES_QUERY, {
    client: midgardClients.get(cKey),
    fetchPolicy: "network-only",
    onError: err => {
      console.error("Error fetching alert rule templates:", err);
      setError("Failed to load alert rule templates. Please try again later.");
      setLoading(false);
    },
    onCompleted: () => {
      setLoading(false);
    },
  });

  // Query to get contact groups
  const { loading: contactsLoading } = useQuery(CONTACTS_QUERY, {
    client: midgardClients.get(cKey),
    fetchPolicy: "network-only",
    onError: err => {
      console.error("Error fetching contact groups:", err);
    },
    onCompleted: data => {
      // Process contact groups into a more usable format
      if (data?.contacts) {
        const groupsMap: Record<string, any[]> = {};

        // Flatten and deduplicate contact groups by name
        data.contacts.forEach((contact: any) => {
          if (contact.contactGroups && Array.isArray(contact.contactGroups)) {
            contact.contactGroups.forEach((group: any) => {
              if (group.name) {
                groupsMap[group.name] = group.members || [];
              }
            });
          }
        });

        setContactGroups(groupsMap);
      }
    },
  });

  // Parse the alert rule templates
  const alertRuleTemplates = data?.askrConfig?.alertRuleTemplates || "";
  const ruleData = useMemo(() => {
    let result: { rules: Array<any> } = { rules: [] };
    try {
      const parsedData = JSON.parse(alertRuleTemplates);
      if (parsedData && typeof parsedData === "object") {
        if (Array.isArray(parsedData)) {
          result = { rules: parsedData };
        } else if (parsedData.rules && Array.isArray(parsedData.rules)) {
          result = parsedData;
        } else {
          result = { rules: [parsedData] };
        }
      }
    } catch (e) {
      console.error("Error parsing alert rule templates:", e);
    }
    return result;
  }, [alertRuleTemplates]);

  // Deep search in nested objects
  const deepSearch = useCallback((obj: any, term: string): boolean => {
    if (!obj) {
      return false;
    }
    if (!term) {
      return true;
    }

    const lowerTerm = term.toLowerCase();

    // Handle primitive values
    if (typeof obj === "string") {
      return obj.toLowerCase().includes(lowerTerm);
    }
    if (typeof obj === "number" || typeof obj === "boolean") {
      return String(obj).toLowerCase().includes(lowerTerm);
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.some(item => deepSearch(item, term));
    }

    // Handle objects
    if (typeof obj === "object") {
      return Object.values(obj).some(value => deepSearch(value, term));
    }

    return false;
  }, []);

  // Check if a rule contains the search term in its expanded content
  const ruleMatchesSearchInExpandedContent = useCallback(
    (rule: any, term: string): boolean => {
      if (!term) {
        return false;
      }

      // Search in conditions
      if (rule.conditions && deepSearch(rule.conditions, term)) {
        return true;
      }

      // Search in actions
      if (rule.actions && deepSearch(rule.actions, term)) {
        return true;
      }

      // Search in associated contact groups for recipients
      if (rule.actions && Array.isArray(rule.actions)) {
        for (const action of rule.actions) {
          if (action.type === "alert" && action.args?.deliveries) {
            for (const delivery of action.args.deliveries) {
              if (delivery.contactGroup) {
                const members = contactGroups[delivery.contactGroup] || [];
                for (const member of members) {
                  if (
                    member.email &&
                    member.email.toLowerCase().includes(term.toLowerCase())
                  ) {
                    return true;
                  }
                }
              }
            }
          }
        }
      }

      return false;
    },
    [contactGroups, deepSearch]
  );

  // Toggle row expansion
  const toggleRowExpansion = useCallback((index: number) => {
    setExpandedRows(prevExpandedRows => {
      if (prevExpandedRows.includes(index)) {
        return prevExpandedRows.filter(rowIndex => rowIndex !== index);
      } else {
        return [...prevExpandedRows, index];
      }
    });
  }, []);

  // Handle search input changes
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    []
  );

  // Clear search field
  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
  }, []);

  // Format complex objects in a readable way
  const formatValue = (value: any): string => {
    if (value === null) {
      return "null";
    }
    if (value === undefined) {
      return "undefined";
    }

    if (typeof value === "object") {
      // Special handling for productionSegmentFilter objects when called with the object
      if (
        value.tagPaths ||
        (Object.keys(value).length === 1 &&
          (Object.keys(value)[0] === "productionSegmentFilter" ||
            Object.keys(value)[0] === "tagPaths"))
      ) {
        const paths =
          value.tagPaths ?? value.productionSegmentFilter?.tagPaths ?? [];
        if (Array.isArray(paths)) {
          // Join with newline for potential list rendering if called on the object
          return paths
            .map((tag: any) => {
              if (typeof tag !== "string") {
                return String(tag);
              }
              // Apply comma-to-slash logic here too, in case called on object
              const parts = tag.split(",");
              return parts.map(part => part.replace(/\[\]"/g, "")).join("/");
            })
            .join("\n");
        }
        return String(
          value.tagPaths ?? value.productionSegmentFilter?.tagPaths
        );
      }

      // Handle generic arrays
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return "[]";
        }
        return value.map(item => formatValue(item)).join("/"); // Join with slash for generic arrays
      }

      // Handle generic objects
      const pairs = Object.entries(value).map(
        ([k, v]) => `${k}: ${formatValue(v)}`
      );
      if (pairs.length === 0) {
        return "{}";
      }
      return pairs.join(", ");
    }

    // Handle strings specifically (comma-to-slash conversion)
    if (typeof value === "string" && value.includes(",")) {
      const parts = value.split(",");
      return parts.map(part => part.replace(/\[\]"/g, "")).join("/");
    }

    // Handle other primitive types
    return String(value);
  };

  // Format complex objects in a readable way for CSV export
  const formatValueForCsv = useCallback((value: any): string => {
    if (value === null) {
      return "null";
    }
    if (value === undefined) {
      return "undefined";
    }
    if (typeof value === "object") {
      if (
        Object.keys(value).length === 1 &&
        (Object.keys(value)[0] === "productionSegmentFilter" ||
          Object.keys(value)[0] === "tagPaths")
      ) {
        const paths =
          value.productionSegmentFilter?.tagPaths ?? value.tagPaths ?? [];
        if (Array.isArray(paths)) {
          return paths
            .map((tag: any) => {
              if (typeof tag !== "string") {
                return String(tag);
              }
              return tag
                .split(",")
                .map(part => part.replace(/\[\]"/g, ""))
                .join("/");
            })
            .join("; "); // Use semicolon for CSV separation
        }
        return JSON.stringify(paths);
      }
      return JSON.stringify(value);
    }
    return String(value);
  }, []);

  // Prepare actions for CSV export
  const formatActionsForCsv = (actions: any[]): string => {
    if (!actions || !Array.isArray(actions) || actions.length === 0) {
      return "";
    }

    return actions
      .map(action => {
        const parts: string[] = [];
        parts.push(`Type: ${action.type || "Unknown"}`);

        if (action.args) {
          if (action.args.deliveries && action.args.deliveries.length > 0) {
            const deliveryMethods = action.args.deliveries
              .map(
                (d: any) =>
                  `${d.method}${d.contactGroup ? ` (${d.contactGroup})` : ""}`
              )
              .join("; ");
            parts.push(`Delivery: ${deliveryMethods}`);
          }

          if (action.args.messageOnRaise) {
            parts.push(`Message: ${action.args.messageOnRaise}`);
          }

          if (action.args.silenceNewAlertsForMinutes) {
            parts.push(
              `Silence: ${action.args.silenceNewAlertsForMinutes} minutes`
            );
          }
        }

        return parts.join(" | ");
      })
      .join("\n");
  };

  // Prepare conditions for CSV export
  const formatConditionsForCsv = useCallback(
    (conditions: any[]): string => {
      if (
        !conditions ||
        !Array.isArray(conditions) ||
        conditions.length === 0
      ) {
        return "";
      }

      return conditions
        .map(condition => {
          const parts: string[] = [];
          parts.push(`Type: ${condition.type || "Unknown"}`);

          if (
            condition.type === "matching-open-production-segments" &&
            condition.args?.productionSegmentFilter?.tagPaths
          ) {
            const tagPaths = condition.args.productionSegmentFilter.tagPaths
              .map((tag: any) =>
                typeof tag === "string"
                  ? tag.replace(/\[\]"/g, "")
                  : String(tag)
              )
              .join("; ");
            parts.push(`Tags: ${tagPaths}`);
          } else if (condition.args) {
            // Handle other condition types generically
            const argPairs = Object.entries(condition.args).map(
              ([key, value]) => `${key}: ${formatValueForCsv(value)}`
            );
            if (argPairs.length > 0) {
              parts.push(argPairs.join(" | "));
            }
          }

          return parts.join(" | ");
        })
        .join("\n");
    },
    [formatValueForCsv]
  );

  // Format condition objects into human-readable descriptions
  const formatCondition = (condition: any): React.ReactNode => {
    if (!condition || typeof condition !== "object") {
      return null;
    }

    const { type, args } = condition;

    // Case 1: Matching production segments with tags
    if (
      type === "matching-open-production-segments" &&
      args?.productionSegmentFilter?.tagPaths
    ) {
      const tagPaths = args.productionSegmentFilter.tagPaths;

      return (
        <Box>
          <Typography
            variant="caption"
            sx={{ fontWeight: "bold", display: "block" }}
          >
            Matching production segments with tags:
          </Typography>
          {tagPaths.map((tag: any, idx: number) => (
            <Typography
              key={idx}
              variant="caption"
              component="div"
              sx={{ ml: 2 }}
            >
              • {formatValue(tag)}
            </Typography>
          ))}
          <Box
            sx={{
              mt: 2,
              p: 1,
              bgcolor: "#f5f7fa",
              borderRadius: 1,
              borderLeft: "3px solid #1976d2",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: "bold",
                display: "block",
                color: "#666",
              }}
            >
              {translate("Explanation")}:
            </Typography>
            <Typography
              variant="caption"
              component="div"
              sx={{ ml: 1, mt: 0.5 }}
            >
              This condition will trigger an alert for any production segment
              {tagPaths.length > 0 && (
                <span>
                  {" "}
                  tagged{" "}
                  {tagPaths.map((tag: any, idx: number) => (
                    <Box key={idx} component="span">
                      {idx > 0 && ", \nor "}
                      <Box
                        component="span"
                        sx={{
                          fontWeight: "medium",
                          color: "#1976d2",
                        }}
                      >
                        {formatValue(tag)}
                      </Box>
                    </Box>
                  ))}
                  {tagPaths.length > 0 && "."}
                </span>
              )}
            </Typography>
          </Box>
        </Box>
      );
    }

    // Case 2: Matching open production segments with process filters and segment filters
    if (
      (type === "matching-open-production-segments" ||
        args?.processFilter?.type === "matching-open-production-segments") &&
      (args?.processFilter || args?.productionSegmentFilter)
    ) {
      const processFilter = args?.processFilter || {};
      const productionSegmentFilter = args?.productionSegmentFilter || {};
      const processIds = processFilter.processIds || [];
      const minDuration = productionSegmentFilter.minDurationMinutes;
      const pnlCategories = productionSegmentFilter.pnlCategoryFilter || [];

      return (
        <Box>
          {processIds.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: "bold", display: "block" }}
              >
                Matching processes:
              </Typography>
              {processIds.map((id: any, idx: number) => (
                <Typography
                  key={idx}
                  variant="caption"
                  component="div"
                  sx={{ ml: 2 }}
                >
                  • Process {id}
                </Typography>
              ))}
            </Box>
          )}

          {minDuration && (
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: "bold", display: "block" }}
              >
                Minimum duration:
              </Typography>
              <Typography variant="caption" component="div" sx={{ ml: 2 }}>
                {minDuration} minutes
              </Typography>
            </Box>
          )}

          {pnlCategories.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: "bold", display: "block" }}
              >
                PnL categories:
              </Typography>
              {Array.isArray(pnlCategories) ? (
                pnlCategories.map((category: any, idx: number) => (
                  <Typography
                    key={idx}
                    variant="caption"
                    component="div"
                    sx={{ ml: 2 }}
                  >
                    • {String(category)}
                  </Typography>
                ))
              ) : (
                <Typography variant="caption" component="div" sx={{ ml: 2 }}>
                  • {String(pnlCategories)}
                </Typography>
              )}
            </Box>
          )}

          <Box
            sx={{
              mt: 2,
              p: 1,
              bgcolor: "#f5f7fa",
              borderRadius: 1,
              borderLeft: "3px solid #1976d2",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: "bold",
                display: "block",
                color: "#666",
              }}
            >
              {translate("Explanation")}:
            </Typography>
            <Typography
              variant="caption"
              component="div"
              sx={{ ml: 1, mt: 0.5 }}
            >
              This condition will trigger alerts for production segments
              {processIds.length > 0 && (
                <span>
                  {" "}
                  on {processIds.length === 1 ? "process" : "processes"}{" "}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: "medium",
                      color: "#1976d2",
                    }}
                  >
                    {processIds.slice(0, 3).join(", ")}
                    {processIds.length > 3 && ", ..."}
                  </Box>
                </span>
              )}
              {pnlCategories.length > 0 && (
                <span>
                  {" "}
                  with {pnlCategories.length === 1
                    ? "category"
                    : "categories"}{" "}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: "medium",
                      color: "#1976d2",
                    }}
                  >
                    {Array.isArray(pnlCategories)
                      ? pnlCategories.slice(0, 2).join(", ")
                      : String(pnlCategories)}
                    {Array.isArray(pnlCategories) &&
                      pnlCategories.length > 2 &&
                      ", ..."}
                  </Box>
                </span>
              )}
              {minDuration && (
                <span>
                  {" "}
                  lasting at least{" "}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: "medium",
                      color: "#1976d2",
                    }}
                  >
                    {minDuration} minutes
                  </Box>
                </span>
              )}
              .
            </Typography>
          </Box>
        </Box>
      );
    }

    // Default rendering for other condition types
    return null;
  };

  // Format action objects into human-readable descriptions
  const formatAction = (action: any): React.ReactNode => {
    if (!action || typeof action !== "object") {
      return null;
    }

    const { type, args } = action;

    if (type === "alert" && args) {
      return (
        <Box>
          {args.deliveries && args.deliveries.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: "bold", display: "block" }}
              >
                {translate("Alert will be sent via")}:
              </Typography>
              {args.deliveries.map((delivery: any, idx: number) => (
                <Typography
                  key={idx}
                  variant="caption"
                  component="div"
                  sx={{ ml: 2 }}
                >
                  • {delivery.method}{" "}
                  {delivery.contactGroup && `(${delivery.contactGroup})`}
                </Typography>
              ))}
            </Box>
          )}

          {args.messageOnRaise && (
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: "bold", display: "block" }}
              >
                {translate("Message")}:
              </Typography>
              <Typography
                variant="caption"
                component="div"
                sx={{
                  ml: 2,
                  p: 1,
                  backgroundColor: "rgba(0,0,0,0.03)",
                  borderRadius: "4px",
                  fontFamily: "monospace",
                }}
              >
                {args.messageOnRaise}
              </Typography>

              {/* Example message with mock values */}
              <Box
                sx={{
                  mt: 2,
                  ml: 2,
                  p: 1,
                  bgcolor: "#f5f7fa",
                  borderRadius: 1,
                  borderLeft: "3px solid #1976d2",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: "bold",
                    display: "block",
                    color: "#666",
                  }}
                >
                  {translate("Example")}:
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ ml: 1, mt: 0.5 }}
                >
                  {args.messageOnRaise
                    .split(/(\{[^}]+\})/g)
                    .map((part, index) => {
                      // Check if this part is a variable placeholder
                      const varMatch = part.match(/\{([^}]+)\}/);
                      if (varMatch) {
                        const varName = varMatch[1];
                        let mockValue = "";

                        // Assign appropriate mock values based on variable name
                        switch (varName) {
                          case "process_name":
                            mockValue = "Assembly Line 2";
                            break;
                          case "tag_name":
                            mockValue = "Temperature";
                            break;
                          case "duration_minutes":
                            mockValue = "15";
                            break;
                          case "alert_count":
                            mockValue = "3";
                            break;
                          case "value":
                            mockValue = "95.2";
                            break;
                          case "threshold":
                            mockValue = "90.0";
                            break;
                          case "unit":
                            mockValue = "°C";
                            break;
                          default:
                            // For any other variables, use a generic mock value
                            mockValue = `mock-${varName}`;
                        }

                        return (
                          <Box
                            key={index}
                            component="span"
                            sx={{
                              fontWeight: "medium",
                              color: "#1976d2",
                            }}
                          >
                            {mockValue}
                          </Box>
                        );
                      }

                      // Return regular text
                      return <span key={index}>{part}</span>;
                    })}
                </Typography>
              </Box>
            </Box>
          )}

          {args.silenceNewAlertsForMinutes && (
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: "bold", display: "block" }}
              >
                {translate("Silence new alerts for")}:
              </Typography>
              <Typography variant="caption" component="div" sx={{ ml: 2 }}>
                {args.silenceNewAlertsForMinutes} {translate("minutes")}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    return null;
  };

  // Filter and sort data
  const filteredAndSortedRules = useMemo(() => {
    if (!ruleData?.rules || !Array.isArray(ruleData.rules)) {
      return [];
    }

    // First apply filtering
    let filteredRules = ruleData.rules;

    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filteredRules = filteredRules.filter(rule => {
        // Search in basic fields
        const basicFieldsMatch =
          (rule.name && rule.name.toLowerCase().includes(lowerSearchTerm)) ||
          (rule.description &&
            rule.description.toLowerCase().includes(lowerSearchTerm)) ||
          (rule.scheduleFrequencySeconds &&
            String(rule.scheduleFrequencySeconds).includes(lowerSearchTerm));

        // Search in expanded content
        const expandedContentMatch = ruleMatchesSearchInExpandedContent(
          rule,
          searchTerm
        );

        return basicFieldsMatch || expandedContentMatch;
      });
    }

    // Then apply sorting - Create a NEW sorted array
    const sortedRules = [...filteredRules].sort((a, b) => {
      let valueA: any;
      let valueB: any;

      if (sortColumn === "name") {
        valueA = a.name || "";
        valueB = b.name || "";
      } else if (sortColumn === "scheduleFrequency") {
        valueA = a.scheduleFrequencySeconds || 0;
        valueB = b.scheduleFrequencySeconds || 0;
      } else if (sortColumn === "actions") {
        valueA = (a.actions || []).length;
        valueB = (b.actions || []).length;
      } else if (sortColumn === "conditions") {
        valueA = (a.conditions || []).length;
        valueB = (b.conditions || []).length;
      } else {
        return 0;
      }

      if (sortDirection === "asc") {
        return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
      } else {
        return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
      }
    });

    // Return the new sorted array
    return sortedRules;
  }, [
    ruleData.rules,
    searchTerm,
    sortColumn,
    sortDirection,
    ruleMatchesSearchInExpandedContent,
  ]);

  // Auto-expand rows with matching content
  useEffect(() => {
    if (!searchTerm) {
      // If search is cleared, collapse all rows
      setExpandedRows([]);
      return;
    }

    const matchingRowIndices: number[] = [];

    // Find rules with matching content in expanded sections
    filteredAndSortedRules.forEach((rule, index) => {
      if (ruleMatchesSearchInExpandedContent(rule, searchTerm)) {
        matchingRowIndices.push(index);
      }
    });

    // Update expanded rows to include matching rows
    setExpandedRows(prevExpandedRows => {
      // Create a new Set to avoid duplicates
      const expandedSet = new Set(prevExpandedRows);
      matchingRowIndices.forEach(index => expandedSet.add(index));
      return Array.from(expandedSet);
    });
  }, [searchTerm, filteredAndSortedRules, ruleMatchesSearchInExpandedContent]);

  // Handle column sort change
  const handleSort = useCallback(
    (column: string) => {
      let newDirection: "asc" | "desc" = "asc";
      if (sortColumn === column) {
        newDirection = sortDirection === "asc" ? "desc" : "asc";
      } else {
        setSortColumn(column);
      }
      setSortDirection(newDirection);
    },
    [sortColumn, sortDirection] // Ensure sortDirection is a dependency
  );

  // Get the current page of data
  const currentPageData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredAndSortedRules.slice(startIndex, endIndex);
  }, [filteredAndSortedRules, page, rowsPerPage]);

  // Export alert rules to CSV
  const handleExportCsv = useCallback(() => {
    if (!filteredAndSortedRules || filteredAndSortedRules.length === 0) {
      return;
    }

    const csvData = filteredAndSortedRules.map(rule => {
      return {
        "Rule Name": rule.name || "Unnamed Rule",
        "Schedule Frequency": rule.scheduleFrequencySeconds
          ? `${rule.scheduleFrequencySeconds} seconds`
          : "Not specified",
        Actions: formatActionsForCsv(rule.actions || []),
        Conditions: formatConditionsForCsv(rule.conditions || []),
      };
    });

    const csvContent = convertToCSV(csvData);
    downloadCSV(
      csvContent,
      `alert-rules-${new Date().toISOString().slice(0, 10)}.csv`
    );
  }, [filteredAndSortedRules, formatConditionsForCsv]);

  // Handle pagination
  const handleChangePage = useCallback(
    (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      setPage(newPage);
    },
    []
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    []
  );

  if (loading || queryLoading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5">
          {translate("Alert Rules Configuration")}
        </Typography>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="300px"
        >
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5">
          {translate("Alert Rules Configuration")}
        </Typography>
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          sx={{ mt: 2 }}
          onClick={() => window.location.reload()}
        >
          {translate("Retry")}
        </Button>
      </Paper>
    );
  }

  // Highlighted text component for search results
  const HighlightedText = ({
    text,
    highlight,
  }: {
    text: string;
    highlight: string;
  }) => {
    if (!highlight.trim() || !text) {
      return <>{text}</>;
    }

    const regex = new RegExp(
      `(${highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    );
    const parts = text.split(regex);

    return (
      <>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <Box
              component="span"
              key={i}
              sx={{
                backgroundColor: "rgba(255, 193, 7, 0.3)",
                fontWeight: "bold",
              }}
            >
              {part}
            </Box>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </>
    );
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2,
        }}
      >
        <div>
          <Typography variant="h5">
            {translate("Alert Rules Configuration")}
          </Typography>
        </div>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Tooltip title={translate("Export to CSV")}>
            <IconButton
              onClick={handleExportCsv}
              disabled={
                !filteredAndSortedRules || filteredAndSortedRules.length === 0
              }
              color="primary"
            >
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Search Field */}
      <Box sx={{ p: 2, bgcolor: "#fafafa", borderBottom: "1px solid #e0e0e0" }}>
        <TextField
          fullWidth
          placeholder={translate("Search alert rules...")}
          value={searchTerm}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {currentPageData.length === 0 ? (
        <Alert severity="info" sx={{ m: 2 }}>
          {searchTerm
            ? translate("No alert rules found matching your search.")
            : translate("No alert rules found.")}
        </Alert>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <TableContainer component={Paper} elevation={1}>
            <Table
              sx={{
                width: "100%",
                borderCollapse: "collapse",
                userSelect: "text",
              }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: "#ebebeb" }}>
                  {/* Rule Name Header */}
                  <TableCell
                    sx={theme => ({
                      fontWeight: "bold",
                      borderBottom: "1px solid #ddd",
                      padding: "12px 12px 12px 44px",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#dddddd", // Restore grey background on hover
                        "& > .MuiTableSortLabel-root": {
                          color: theme.palette.primary.main + " !important", // Restore blue text on hover
                        },
                      },
                    })}
                    onClick={() => handleSort("name")}
                  >
                    <TableSortLabel
                      active={sortColumn === "name"}
                      direction={sortColumn === "name" ? sortDirection : "asc"}
                      sx={theme => ({
                        "& .MuiTableSortLabel-icon": {
                          color: theme.palette.primary.main + " !important",
                        },
                      })}
                    >
                      {translate("Rule Name")}
                    </TableSortLabel>
                  </TableCell>
                  {/* Schedule Frequency Header - Remove sorting */}
                  <TableCell
                    align="center"
                    sx={theme => ({
                      padding: "12px",
                      borderBottom: "1px solid #ddd",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      width: "20%", // Set width
                    })}
                  >
                    {translate("Schedule Frequency")}
                  </TableCell>
                  {/* Conditions Header - Remove sorting */}
                  <TableCell
                    align="center"
                    sx={theme => ({
                      padding: "12px",
                      borderBottom: "1px solid #ddd",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      width: "16%", // Set width
                    })}
                  >
                    {translate("Conditions")}
                  </TableCell>
                  {/* Actions Header - Remove sorting */}
                  <TableCell
                    align="center"
                    sx={theme => ({
                      padding: "12px",
                      borderBottom: "1px solid #ddd",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      width: "14%", // Set width
                    })}
                  >
                    {translate("Actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentPageData.map((rule, index) => (
                  <React.Fragment key={`rule-${index}`}>
                    <TableRow
                      onClick={() => toggleRowExpansion(index)}
                      sx={{
                        cursor: "pointer",
                        // Set background based on expansion state first, then alternating
                        backgroundColor: expandedRows.includes(index)
                          ? "#ebebeb" // Expanded color
                          : index % 2 === 0
                            ? "#ffffff"
                            : "#fafafa", // Alternating color
                        // Override hover effect conditionally
                        "&:hover": {
                          backgroundColor: expandedRows.includes(index)
                            ? "#dddddd"
                            : "#ebebeb",
                        },
                      }}
                    >
                      <TableCell
                        align="left"
                        sx={{
                          padding: "12px 12px 12px 6px",
                          borderBottom: "1px solid #ddd",
                          fontWeight: "bold",
                          verticalAlign: "middle",
                          userSelect: "text",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <IconButton
                            onClick={e => {
                              e.stopPropagation();
                              toggleRowExpansion(index);
                            }}
                            size="small"
                          >
                            {expandedRows.includes(index) ? (
                              <KeyboardArrowDownIcon />
                            ) : (
                              <KeyboardArrowRightIcon />
                            )}
                          </IconButton>
                          <HighlightedText
                            text={rule.name || translate("Unnamed Rule")}
                            highlight={searchTerm}
                          />
                        </Box>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px",
                          borderBottom: "1px solid #ddd",
                          verticalAlign: "middle",
                          userSelect: "text",
                        }}
                      >
                        {rule.scheduleFrequencySeconds
                          ? `${rule.scheduleFrequencySeconds} ${translate("seconds")}`
                          : translate("Not specified")}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px",
                          borderBottom: "1px solid #ddd",
                          verticalAlign: "middle",
                          userSelect: "text",
                        }}
                      >
                        {rule.conditions?.length || 0}{" "}
                        {rule.conditions?.length === 1
                          ? translate("condition")
                          : translate("conditions")}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px",
                          borderBottom: "1px solid #ddd",
                          verticalAlign: "middle",
                          userSelect: "text",
                        }}
                      >
                        {rule.actions?.length || 0}{" "}
                        {rule.actions?.length === 1
                          ? translate("action")
                          : translate("actions")}
                      </TableCell>
                    </TableRow>

                    {/* Expanded row content */}
                    {expandedRows.includes(index) && (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{
                            padding: 0,
                            backgroundColor: "#fafafa",
                            userSelect: "text",
                            borderBottom: "none",
                          }}
                        >
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "repeat(3, 1fr)",
                              gap: 2,
                              padding: 2,
                            }}
                          >
                            {/* Conditions section */}
                            {rule.conditions && rule.conditions.length > 0 && (
                              <Box
                                sx={{
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                  p: 1.5,
                                  bgcolor: "white",
                                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "text.secondary",
                                    mb: 1,
                                  }}
                                >
                                  {translate("Conditions")}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                  }}
                                >
                                  {rule.conditions.map(
                                    (condition, condIndex) => {
                                      return (
                                        <Box
                                          key={condIndex}
                                          sx={{
                                            mb: 1,
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight: "bold",
                                              mb: 0.5,
                                              color: "#1976d2",
                                            }}
                                          >
                                            <HighlightedText
                                              text={startCase(
                                                condition.type ||
                                                  translate("Unknown Condition")
                                              )}
                                              highlight={searchTerm}
                                            />
                                          </Typography>

                                          {/* Show formatted condition if available */}
                                          {formatCondition(condition)}

                                          {/* Otherwise show parameters */}
                                          {!formatCondition(condition) &&
                                            condition.args && (
                                              <Box sx={{ mt: 1 }}>
                                                {Object.entries(
                                                  condition.args
                                                ).map(([key, value]) => (
                                                  <Box
                                                    key={key}
                                                    sx={{ mb: 0.5 }}
                                                  >
                                                    <Typography
                                                      variant="caption"
                                                      sx={{
                                                        fontWeight: "bold",
                                                        display: "block",
                                                      }}
                                                    >
                                                      {key}:
                                                    </Typography>
                                                    <Typography
                                                      variant="caption"
                                                      component="div"
                                                      sx={{ ml: 1 }}
                                                    >
                                                      {formatValue(value)}
                                                    </Typography>
                                                  </Box>
                                                ))}
                                              </Box>
                                            )}
                                        </Box>
                                      );
                                    }
                                  )}
                                </Box>
                              </Box>
                            )}

                            {/* Actions section */}
                            {rule.actions && rule.actions.length > 0 && (
                              <Box
                                sx={{
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                  p: 1.5,
                                  bgcolor: "white",
                                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "text.secondary",
                                    mb: 1,
                                  }}
                                >
                                  {translate("Actions")}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                  }}
                                >
                                  {rule.actions.map((action, actionIndex) => (
                                    <Box
                                      key={actionIndex}
                                      sx={{
                                        mb: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: "bold",
                                          mb: 0.5,
                                          color: "#1976d2",
                                        }}
                                      >
                                        <HighlightedText
                                          text={
                                            action.type ||
                                            translate("Unknown Type")
                                          }
                                          highlight={searchTerm}
                                        />
                                      </Typography>

                                      {/* Show formatted action if available */}
                                      {formatAction(action)}

                                      {/* Otherwise show raw details */}
                                      {!formatAction(action) && action.args && (
                                        <Box sx={{ mt: 1 }}>
                                          {/* Deliveries */}
                                          {action.args.deliveries &&
                                            action.args.deliveries.length >
                                              0 && (
                                              <Box sx={{ mb: 1 }}>
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    fontWeight: "bold",
                                                    display: "block",
                                                  }}
                                                >
                                                  {translate("Delivery Method")}
                                                  :
                                                </Typography>
                                                {action.args.deliveries.map(
                                                  (delivery, i) => (
                                                    <Box
                                                      key={i}
                                                      sx={{ ml: 1, mt: 0.5 }}
                                                    >
                                                      <Typography
                                                        variant="caption"
                                                        component="div"
                                                      >
                                                        {delivery.method}{" "}
                                                        {delivery.contactGroup &&
                                                          `(${delivery.contactGroup})`}
                                                      </Typography>
                                                    </Box>
                                                  )
                                                )}
                                              </Box>
                                            )}

                                          {/* Message */}
                                          {action.args.messageOnRaise && (
                                            <Box sx={{ mb: 1 }}>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  fontWeight: "bold",
                                                  display: "block",
                                                }}
                                              >
                                                {translate("Message")}:
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                component="div"
                                                sx={{ ml: 1, mt: 0.5 }}
                                              >
                                                <HighlightedText
                                                  text={
                                                    action.args.messageOnRaise
                                                  }
                                                  highlight={searchTerm}
                                                />
                                              </Typography>

                                              {/* Example message with mock values */}
                                              <Box
                                                sx={{
                                                  mt: 1,
                                                  p: 1,
                                                  bgcolor: "#f5f7fa",
                                                  borderRadius: 1,
                                                  borderLeft:
                                                    "3px solid #1976d2",
                                                }}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    fontWeight: "bold",
                                                    display: "block",
                                                    color: "#666",
                                                  }}
                                                >
                                                  {translate("Example")}:
                                                </Typography>
                                                <Typography
                                                  variant="caption"
                                                  component="div"
                                                  sx={{ ml: 1, mt: 0.5 }}
                                                >
                                                  {action.args.messageOnRaise
                                                    .split(/(\{[^}]+\})/g)
                                                    .map((part, index) => {
                                                      // Check if this part is a variable placeholder
                                                      const varMatch =
                                                        part.match(
                                                          /\{([^}]+)\}/
                                                        );
                                                      if (varMatch) {
                                                        const varName =
                                                          varMatch[1];
                                                        let mockValue = "";

                                                        // Assign appropriate mock values based on variable name
                                                        switch (varName) {
                                                          case "process_name":
                                                            mockValue =
                                                              "Assembly Line 2";
                                                            break;
                                                          case "tag_name":
                                                            mockValue =
                                                              "Temperature";
                                                            break;
                                                          case "duration_minutes":
                                                            mockValue = "15";
                                                            break;
                                                          case "alert_count":
                                                            mockValue = "3";
                                                            break;
                                                          case "value":
                                                            mockValue = "95.2";
                                                            break;
                                                          case "threshold":
                                                            mockValue = "90.0";
                                                            break;
                                                          case "unit":
                                                            mockValue = "°C";
                                                            break;
                                                          default:
                                                            // For any other variables, use a generic mock value
                                                            mockValue = `mock-${varName}`;
                                                        }

                                                        return (
                                                          <Box
                                                            key={index}
                                                            component="span"
                                                            sx={{
                                                              fontWeight:
                                                                "medium",
                                                              color: "#1976d2",
                                                            }}
                                                          >
                                                            {mockValue}
                                                          </Box>
                                                        );
                                                      }

                                                      // Return regular text
                                                      return (
                                                        <span key={index}>
                                                          {part}
                                                        </span>
                                                      );
                                                    })}
                                                </Typography>
                                              </Box>
                                            </Box>
                                          )}

                                          {/* Silence duration */}
                                          {action.args
                                            .silenceNewAlertsForMinutes && (
                                            <Box>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  fontWeight: "bold",
                                                  display: "block",
                                                }}
                                              >
                                                {translate("Silence Duration")}:
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                component="div"
                                                sx={{ ml: 1 }}
                                              >
                                                {
                                                  action.args
                                                    .silenceNewAlertsForMinutes
                                                }{" "}
                                                {translate("minutes")}
                                              </Typography>
                                            </Box>
                                          )}
                                        </Box>
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            )}

                            {/* Recipients section */}
                            {rule.actions && rule.actions.length > 0 && (
                              <Box>
                                {/* Recipients section */}
                                {/* Only show recipients if there are alert actions with deliveries */}
                                <Box
                                  sx={{
                                    border: "1px solid #ddd",
                                    borderRadius: "4px",
                                    p: 1.5,
                                    bgcolor: "white",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "text.secondary",
                                      mb: 1,
                                    }}
                                  >
                                    {translate("Recipients")}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 1,
                                    }}
                                  >
                                    {contactsLoading ? (
                                      <Box sx={{ p: 2, textAlign: "center" }}>
                                        <CircularProgress size={24} />
                                      </Box>
                                    ) : (
                                      <>
                                        {rule.actions.map(
                                          (action, actionIndex) => {
                                            // Only process alert actions with deliveries
                                            if (
                                              action.type !== "alert" ||
                                              !action.args?.deliveries ||
                                              !action.args.deliveries.length
                                            ) {
                                              return null;
                                            }

                                            // Get all contact groups from this action's deliveries
                                            const actionContactGroups =
                                              action.args.deliveries
                                                .map(
                                                  (delivery: any) =>
                                                    delivery.contactGroup
                                                )
                                                .filter(Boolean);

                                            if (
                                              actionContactGroups.length === 0
                                            ) {
                                              return null;
                                            }

                                            return (
                                              <React.Fragment key={actionIndex}>
                                                {actionContactGroups.map(
                                                  (
                                                    groupName: string,
                                                    groupIndex: number
                                                  ) => {
                                                    const members =
                                                      contactGroups[
                                                        groupName
                                                      ] || [];
                                                    if (!members.length) {
                                                      return null;
                                                    }

                                                    return (
                                                      <Box
                                                        key={`${actionIndex}-${groupIndex}`}
                                                        sx={{
                                                          mb: 1,
                                                        }}
                                                      >
                                                        <Typography
                                                          variant="body2"
                                                          sx={{
                                                            fontWeight: "bold",
                                                            mb: 0.5,
                                                            color: "#1976d2",
                                                          }}
                                                        >
                                                          <HighlightedText
                                                            text={groupName}
                                                            highlight={
                                                              searchTerm
                                                            }
                                                          />
                                                        </Typography>
                                                        <Box sx={{ mt: 1 }}>
                                                          {members.map(
                                                            (
                                                              member: any,
                                                              memberIndex: number
                                                            ) => (
                                                              <Typography
                                                                key={
                                                                  memberIndex
                                                                }
                                                                variant="caption"
                                                                component="div"
                                                                sx={{
                                                                  ml: 1,
                                                                  mb: 0.5,
                                                                  display:
                                                                    "flex",
                                                                  alignItems:
                                                                    "center",
                                                                }}
                                                              >
                                                                <Box
                                                                  component="span"
                                                                  sx={{
                                                                    width:
                                                                      "6px",
                                                                    height:
                                                                      "6px",
                                                                    borderRadius:
                                                                      "50%",
                                                                    backgroundColor:
                                                                      "#1976d2",
                                                                    display:
                                                                      "inline-block",
                                                                    mr: 1,
                                                                  }}
                                                                />
                                                                <HighlightedText
                                                                  text={
                                                                    member.email
                                                                  }
                                                                  highlight={
                                                                    searchTerm
                                                                  }
                                                                />
                                                              </Typography>
                                                            )
                                                          )}
                                                        </Box>
                                                      </Box>
                                                    );
                                                  }
                                                )}
                                              </React.Fragment>
                                            );
                                          }
                                        )}
                                        {/* Show a message if no recipients are found */}
                                        {rule.actions.every(
                                          action =>
                                            action.type !== "alert" ||
                                            !action.args?.deliveries ||
                                            !action.args.deliveries.length ||
                                            !action.args.deliveries.some(
                                              (d: any) =>
                                                d.contactGroup &&
                                                contactGroups[d.contactGroup]
                                                  ?.length
                                            )
                                        ) && (
                                          <Box
                                            sx={{
                                              mb: 1,
                                            }}
                                          >
                                            <Typography
                                              variant="body2"
                                              sx={{ color: "text.secondary" }}
                                            >
                                              {translate(
                                                "No recipients found for this alert"
                                              )}
                                            </Typography>
                                          </Box>
                                        )}
                                      </>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredAndSortedRules.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              borderTop: "1px solid #e0e0e0",
              ".MuiTablePagination-toolbar": {
                minHeight: "52px",
              },
            }}
          />
        </Box>
      )}

      {process.env.NODE_ENV === "development" && (
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            * JSON viewer below is only visible in development mode
          </Typography>
          <CustomJsonEditor
            key={`json-editor-alert-rules ${JSON.stringify(ruleData)} ${JSON.stringify(contactGroups)}`}
            value={{ ...(ruleData || {}), contactGroups }}
            height={innerHeight ? innerHeight - 300 : 500}
          />
        </Box>
      )}
    </Paper>
  );
};
